import { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useApp } from '@/components/AppLayout';
import { Package, X, Search } from 'lucide-react';
import {
  itemApi,
  itemBundleApi,
  taxApi,
  type Item,
  type ItemBundle,
  type CreateItemBundleData,
  type Tax,
} from '@/lib/api';
import { formatAmount } from '@/lib/currency';

interface SelectedItem {
  item: Item;
  quantity: string;
}

interface BundleDialogProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  bundle?: ItemBundle | null;
  onSaved: () => void;
}

export function BundleDialog({ open, onClose, businessId, bundle, onSaved }: BundleDialogProps) {
  const { currentBusiness } = useApp();
  const isEditing = !!bundle;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [availableItems, setAvailableItems] = useState<Item[]>([]);
  const [availableTaxes, setAvailableTaxes] = useState<Tax[]>([]);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemSearchOpen, setItemSearchOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load available items and taxes when dialog opens
  useEffect(() => {
    if (open && businessId) {
      itemApi.list(businessId).then((response) => {
        if (response.data?.results) {
          setAvailableItems(response.data.results);
        }
      });
      taxApi.list(businessId).then((response) => {
        if (response.data) {
          setAvailableTaxes(response.data);
        }
      });
    }
  }, [open, businessId]);

  const getTaxDisplay = (taxIds: string[]): string => {
    if (!taxIds || taxIds.length === 0) return '-';
    const taxNames = taxIds
      .map((id) => availableTaxes.find((t) => t.id === id))
      .filter(Boolean)
      .map((t) => `${t!.name} ${t!.rate}%`);
    return taxNames.length > 0 ? taxNames.join(', ') : '-';
  };

  // Populate form when editing or reset when adding
  useEffect(() => {
    if (open) {
      if (bundle) {
        setName(bundle.name);
        setDescription(bundle.description || '');
        // Convert bundle links to selected items
        const items: SelectedItem[] = bundle.links.map((link) => ({
          item: {
            id: link.item,
            name: link.item_name,
            rate: link.item_rate,
            item_type: link.item_type,
            code: '',
            description: '',
            default_quantity: '1',
            discount: '0',
            discount_type: 'percentage',
            taxes: [],
            quantity_label: null,
            quantity_label_text: null,
            business: businessId,
            created_at: '',
            updated_at: '',
          },
          quantity: link.quantity,
        }));
        setSelectedItems(items);
      } else {
        // Reset form for adding new bundle
        setName('');
        setDescription('');
        setSelectedItems([]);
        setItemSearchQuery('');
        setItemSearchOpen(false);
      }
      setError('');
    }
  }, [open, bundle, businessId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setItemSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter items for autocomplete (exclude already selected)
  const filteredItems = availableItems.filter(
    (item) =>
      !selectedItems.some((s) => s.item.id === item.id) &&
      item.name.toLowerCase().includes(itemSearchQuery.toLowerCase())
  );

  const handleAddItem = (item: Item) => {
    setSelectedItems([
      ...selectedItems,
      {
        item,
        quantity: item.default_quantity || '1',
      },
    ]);
    setItemSearchQuery('');
    setItemSearchOpen(false);
  };

  const handleRemoveItem = (itemId: string) => {
    setSelectedItems(selectedItems.filter((s) => s.item.id !== itemId));
  };

  const handleQuantityChange = (itemId: string, quantity: string) => {
    setSelectedItems(
      selectedItems.map((s) => (s.item.id === itemId ? { ...s, quantity } : s))
    );
  };

  const getItemTypeLabel = (itemType: string): string => {
    switch (itemType) {
      case 'product_parts':
        return 'Parts';
      case 'labor':
        return 'Labor';
      case 'other':
        return 'Other';
      default:
        return itemType;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const data: CreateItemBundleData = {
      name,
      description: description || undefined,
      business: businessId,
      links_write: selectedItems.map((s, i) => ({
        item: s.item.id,
        quantity: s.quantity,
        sort_order: i,
      })),
    };

    const response = isEditing
      ? await itemBundleApi.update(bundle!.id, data)
      : await itemBundleApi.create(data);

    setSubmitting(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    onSaved();
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Full-screen overlay */}
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
            <div className="h-6 w-px bg-border" />
            <span className="font-medium text-sm">{currentBusiness?.name}</span>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col items-center justify-center px-4 py-16">
            {/* Centered content container */}
            <div className="w-full max-w-md">
              <h1 className="text-2xl mb-8 flex items-center gap-2">
                {isEditing ? 'Edit Bundle' : 'Add Bundle'}
                <Package className="h-5 w-5 text-blue-400" />
              </h1>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2 group">
                  <Label
                    htmlFor="dialog-name"
                    className="text-muted-foreground group-focus-within:text-foreground transition-colors"
                  >
                    Bundle name
                  </Label>
                  <Input
                    id="dialog-name"
                    placeholder="e.g., Standard Service Package"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2 group">
                  <Label
                    htmlFor="dialog-description"
                    className="text-muted-foreground group-focus-within:text-foreground transition-colors"
                  >
                    Description (optional)
                  </Label>
                  <Textarea
                    id="dialog-description"
                    placeholder="What's included in this bundle"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="resize-none field-sizing-content"
                  />
                </div>

                <hr className="border-border" />

                {/* Item Search Section */}
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Items in bundle</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      placeholder="Search items to add..."
                      value={itemSearchQuery}
                      onChange={(e) => setItemSearchQuery(e.target.value)}
                      onFocus={() => setItemSearchOpen(true)}
                      className="pl-9"
                    />
                    {itemSearchOpen && (
                      <div
                        ref={dropdownRef}
                        className="absolute top-full left-0 w-full mt-1 bg-popover border rounded-md shadow-md z-50 max-h-60 overflow-auto"
                      >
                        {filteredItems.length > 0 ? (
                          filteredItems.map((item) => (
                            <div
                              key={item.id}
                              className="px-3 py-3 hover:bg-accent cursor-pointer flex items-center justify-between text-blue-600"
                              onClick={() => handleAddItem(item)}
                            >
                              <div className="min-w-0 flex-1 flex items-center gap-2">
                                <span className="truncate">{item.name}</span>
                                {item.code && (
                                  <span className="text-blue-400 text-sm shrink-0">{item.code}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 ml-3 shrink-0">
                                <span className="text-sm">${item.rate}</span>
                                <span className="text-xs text-muted-foreground">
                                  {getItemTypeLabel(item.item_type)}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-3 text-center text-sm text-muted-foreground">
                            {itemSearchQuery
                              ? 'No items found'
                              : selectedItems.length === availableItems.length
                              ? 'All items added'
                              : 'Type to search items'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Items Table */}
                {selectedItems.length > 0 && (
                  <Table className="mb-2">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="h-8 px-0">Item</TableHead>
                        <TableHead className="h-8 px-2 w-20">Qty</TableHead>
                        <TableHead className="h-8 px-2">Tax</TableHead>
                        <TableHead className="h-8 px-2 text-right">Rate</TableHead>
                        <TableHead className="h-8 px-0 w-8"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedItems.map((selected) => (
                        <TableRow key={selected.item.id} className="hover:bg-transparent">
                          <TableCell className="py-2 px-0">{selected.item.name}</TableCell>
                          <TableCell className="py-2 px-2">
                            <Input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={selected.quantity}
                              onChange={(e) =>
                                handleQuantityChange(selected.item.id, e.target.value)
                              }
                              className="w-24 h-7 text-center text-sm"
                            />
                          </TableCell>
                          <TableCell className="py-2 px-2 text-muted-foreground">
                            {getTaxDisplay(selected.item.taxes)}
                          </TableCell>
                          <TableCell className="py-2 px-2 text-right">${selected.item.rate}</TableCell>
                          <TableCell className="py-2 px-0">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(selected.item.id)}
                              className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                              aria-label="Remove item"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {selectedItems.length > 0 && (
                  <div className="flex justify-between items-center py-2 text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="pr-10">
                      {formatAmount(selectedItems
                        .reduce((sum, s) => sum + parseFloat(s.quantity || '0') * parseFloat(s.item.rate || '0'), 0))}
                    </span>
                  </div>
                )}

                {selectedItems.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Search and add items to this bundle
                  </p>
                )}

                {error && <p className="text-sm text-destructive">{error}</p>}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1 rounded-full"
                  >
                    Go Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 rounded-full"
                    disabled={submitting || !name}
                  >
                    {submitting
                      ? isEditing
                        ? 'Saving...'
                        : 'Creating...'
                      : isEditing
                      ? 'Save Changes'
                      : 'Create Bundle'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
